import React, { useRef, useState } from 'react';
import { ContextMenu, ContextMenuAction } from './ContextMenu';
import { useFieldHistory } from './FieldHistoryContext';

interface FieldMenuProps {
  children: React.ReactNode;
  /** Already-resolved `cms.history.colname` values (see resolveHistoryFields). */
  historyFields?: string[];
  /** Current field value (already display-formatted); enables "Copy Value". */
  value?: string;
  /** Data-management table maintaining this field; enables "Table Maintenance". */
  maintenance?: { label: string; url: string };
  className?: string;
}

// Only labels that actually carry a menu get this, so the color change is a
// truthful affordance: it appears exactly where a right-click does something.
const HOVER_CLASS = 'hover:text-blue-600 dark:hover:text-blue-400 transition-colors';

export const FieldMenu: React.FC<FieldMenuProps> = ({
  children,
  historyFields,
  value,
  maintenance,
  className,
}) => {
  const fieldHistory = useFieldHistory();
  const [menuAnchor, setMenuAnchor] = useState<{ x: number; y: number } | null>(null);
  const spanRef = useRef<HTMLSpanElement>(null);

  const handleContextMenu = (event: React.MouseEvent<HTMLSpanElement>) => {
    event.preventDefault();
    setMenuAnchor({ x: event.clientX, y: event.clientY });
  };

  const text = spanRef.current?.textContent ?? '';

  const actions: ContextMenuAction[] = [
    {
      label: 'Copy Label',
      onSelect: () => {
        navigator.clipboard.writeText(text);
      },
    },
  ];

  if (value !== undefined) {
    actions.push({
      label: 'Copy Value',
      onSelect: () => {
        navigator.clipboard.writeText(value);
      },
    });
  }

  if (fieldHistory && historyFields && historyFields.length > 0 && menuAnchor) {
    actions.push({
      label: 'View History',
      onSelect: () => {
        fieldHistory.openPanel({ colnames: historyFields, title: text, anchor: menuAnchor });
      },
    });
  }

  if (maintenance) {
    actions.push({
      label: 'Table Maintenance',
      title: `Open ${maintenance.label}`,
      separator: true,
      onSelect: () => {
        window.open(maintenance.url, '_blank', 'noopener');
      },
    });
  }

  return (
    <>
      <span
        ref={spanRef}
        className={`${HOVER_CLASS} ${className ?? ''}`}
        onContextMenu={handleContextMenu}
      >
        {children}
      </span>
      {menuAnchor && (
        <ContextMenu
          x={menuAnchor.x}
          y={menuAnchor.y}
          header={text}
          actions={actions}
          onClose={() => setMenuAnchor(null)}
        />
      )}
    </>
  );
};
